import { createListCollection, Select as ArkSelect } from "@ark-ui/react";
import { Layers as LayersIcon } from "@mui/icons-material";
import { ChevronDown } from "lucide-react";
import classnames from "classnames";
import { useCallback, useMemo } from "react";

import Select from "@/components/select/select";
import Stack from "@/components/stack/stack";
import { EnumDimension } from "@/lib/dimensions/enum-dimension";
import { MapLayersController } from "@/lib/map-layers";
import { EnumPanelContainer } from "@/features/shared/widgets/map/map.styles";
import { useTranslation } from "@/lib/i18n";

import styles from "./enum-control.module.css";
import panelCommonStyles from "./panel-common.module.css";

type EnumControlProps = MapLayersController & {
  /** Lets the parent map control match the panel to its own chrome. */
  className?: string;
};

const EnumControl = ({
  dimensions,
  currentDimensionValues,
  setDimensionValue,
  className,
}: EnumControlProps) => {
  const { t } = useTranslation();
  const enumDimensions = useMemo(() => {
    return dimensions.filter((it) => it instanceof EnumDimension);
  }, [dimensions]);

  const handleChange = useCallback(
    (dimensionId: string, value: string) => {
      const dimension = enumDimensions.find((d) => d.id === dimensionId) as EnumDimension;
      if (!dimension) return;

      const dimensionValue = dimension.values.find((v) => v.queryStringValue === value);
      if (!dimensionValue) return;

      setDimensionValue(dimension.id, dimensionValue);
    },
    [enumDimensions, setDimensionValue],
  );

  const enumOptions = useMemo(
    () =>
      enumDimensions.map((dimension) => ({
        dimension,
        collection: createListCollection({
          items: dimension.values.map((value) => ({
            label: `${value.content}${dimension.unit?.symbol ?? ""}`,
            value: value.queryStringValue,
          })),
        }),
      })),
    [enumDimensions],
  );

  if (enumDimensions.length === 0) return null;

  return (
    <EnumPanelContainer
      className={classnames(styles.container, panelCommonStyles.frostedPanel, className)}
    >
      {enumOptions.map(({ dimension, collection }) => {
        const selectedValue =
          currentDimensionValues[dimension.id]?.queryStringValue ??
          dimension.default?.queryStringValue ??
          dimension.values[0]?.queryStringValue;
        const selectedItem = collection.find(selectedValue ?? "") ?? collection.items[0];

        return (
          <div
            className={classnames(styles.row, panelCommonStyles.rowDivider)}
            key={dimension.id}
          >
            <Select
              collection={collection}
              value={selectedValue}
              onValueChange={(value) => handleChange(dimension.id, value)}
              positioning={{ sameWidth: false, offset: { mainAxis: 8, crossAxis: 0 } }}
              trigger={
                <Stack flexDirection="row" gap={1} className={styles.trigger}>
                  <LayersIcon fontSize="small" />
                  <strong className={styles.dimensionLabel}>
                    {t(`map.dimension_names.${dimension.id}`, {
                      defaultValue: dimension.id,
                    })}
                  </strong>
                  <span className={styles.dimensionValue}>{selectedItem?.label}</span>
                  {/* Ark indicator (not a bare icon) so it flips on open, like Select's own */}
                  <ArkSelect.Indicator className={styles.chevron}>
                    <ChevronDown size={16} />
                  </ArkSelect.Indicator>
                </Stack>
              }
              variant="ghost"
              onlyLightTheme
            />
          </div>
        );
      })}
    </EnumPanelContainer>
  );
};

export default EnumControl;
